/**
 * Authorizing a change to the Journal Lock setting (F-JP-06).
 *
 * The decision itself is `planJournalLockChange` in `@ff/domain` — pure, and
 * proven there. What lives here is the *transaction* around it: re-reading
 * availability at the start, raising exactly one challenge, persisting only on
 * an authorized outcome, and re-reading availability again after a failure.
 *
 * The property that matters more than any other in this file is that it never
 * fails open. With authentication unavailable the only permitted change is
 * turning an **existing** lock **off** from an already-verified foreground
 * session; every other combination leaves the persisted setting untouched,
 * including the case where the user is now locked out of their own journal.
 * Being locked out is recoverable. A journal that quietly unlocks itself is
 * not.
 *
 * A lease can never turn a lock **on**. That is enforced by the domain plan and
 * asserted here as well, because this is the layer that would have to *write*
 * the setting for such a bug to have consequences.
 */

import type {
  JournalAuthenticationAvailability,
  JournalLockChangeOutcome,
} from "@ff/domain";
import { planJournalLockChange, resolveJournalLockChange } from "@ff/domain";
import type { JournalAuthReason } from "@ff/domain/ports";

export interface JournalLockChangeRequest {
  /** The persisted value. Never a view's optimistic copy of it. */
  readonly current: boolean;
  readonly requested: boolean;
  readonly isUnlocked: boolean;
  readonly hasVerifiedForegroundLease: boolean;
  /**
   * F-JP-02 re-reads availability at the start of every lock-change
   * authorization. A cached value could be minutes old — long enough for a
   * passcode to have been removed — and the whole decision is made on it.
   */
  readonly readAvailability: () => Promise<JournalAuthenticationAvailability>;
  readonly verify: (reason: JournalAuthReason) => Promise<boolean>;
  /**
   * Writes the setting. Returns whether the write landed: a refused write means
   * the toggle must revert, because the durable value is still the old one.
   */
  readonly persist: (lockEnabled: boolean) => boolean;
}

export interface JournalLockChangeResult {
  readonly outcome: JournalLockChangeOutcome;
  /** Availability as this authorization last read it, for the status copy. */
  readonly availability: JournalAuthenticationAvailability;
  /** Whether the persisted setting actually changed. */
  readonly persisted: boolean;
}

/**
 * Which reason string the system prompt is raised under.
 *
 * `planJournalLockChange` already returns the copy for this, but the
 * verification port takes the *key* into `JOURNAL_AUTH_REASONS` rather than the
 * sentence — deliberately, so a call site cannot invent an unreviewed prompt
 * string. The direction rule is restated here rather than reverse-mapping the
 * sentence back to its key, because a string lookup that silently missed would
 * fall back to some other prompt entirely.
 */
export function journalLockChangeReason(requested: boolean): JournalAuthReason {
  return requested ? "turnLockOn" : "turnLockOff";
}

export async function authorizeJournalLockChange(
  request: JournalLockChangeRequest,
): Promise<JournalLockChangeResult> {
  const availability = await request.readAvailability();

  const plan = planJournalLockChange({
    current: request.current,
    requested: request.requested,
    availability,
    isUnlocked: request.isUnlocked,
    hasVerifiedForegroundLease: request.hasVerifiedForegroundLease,
  });

  switch (plan.kind) {
    case "noChange":
      return { outcome: { kind: "noChange" }, availability, persisted: false };

    case "authenticationUnavailable":
      // The setting is not touched. This is the locked-out case, and it is the
      // outcome the copy has to explain rather than the one to work around.
      return {
        outcome: { kind: "authenticationUnavailable" },
        availability,
        persisted: false,
      };

    case "authorizedByForegroundLease":
      // The one escape hatch, and only ever in the off direction. The domain
      // plan will not produce this for a turn-on; the assertion is here because
      // this is the line that would do the damage if it ever did.
      if (request.requested) {
        return {
          outcome: { kind: "authenticationUnavailable" },
          availability,
          persisted: false,
        };
      }
      return {
        outcome: { kind: "authorizedByForegroundLease" },
        availability,
        persisted: request.persist(false),
      };

    case "requiresAuthentication": {
      const verified = await request.verify(
        journalLockChangeReason(request.requested),
      );
      const outcome = resolveJournalLockChange(plan, verified);
      if (outcome.kind === "authorizedByAuthentication") {
        return {
          outcome,
          availability,
          persisted: request.persist(request.requested),
        };
      }
      // F-JP-02: availability is re-read after any failed authentication. A
      // failure is the most likely moment for it to have changed — a removed
      // passcode presents as a declined prompt — and the status line the user
      // is about to read is written from this value.
      return {
        outcome,
        availability: await request.readAvailability(),
        persisted: false,
      };
    }
  }
}
